import { useState, useMemo, useEffect } from 'react';
import {
    Container,
    Text,
    Group,
    Stack,
    Paper, RingProgress,
    Center,
    Alert,
    Card,
    Title,
    Badge,
    Progress,
    ActionIcon,
    Collapse,
    Box,
    Skeleton,
    UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import { PageHeader } from '../common/PageHeader';
import {
  Objective,
  KeyActivity,
  Activity,
  Milestone,
  MilestoneData,
  MilestoneStatus,
  ComponentProjectMilestoneData,
  ObjectivesMetadata,
} from '../../types/milestones';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  milestonesApi,
  getMilestonesForAllSubawardees,
  getMilestonesForAllComponentProjects,
} from '../../api/milestones';
import { useAdminMilestoneViewUrlState } from '../../hooks/useAdminMilestoneViewUrlState';
import { useSyncSelectedGroupForMilestoneView } from '../../hooks/useSyncSelectedGroupForMilestoneView';
import { AdminMilestonesFilters, type AdminMilestonesFiltersProps } from './AdminMilestonesFilters';
import { MilestoneDateLabels, milestoneHasSecondaryDateLabel, MILESTONE_SECONDARY_DATE_ROW_OFFSET } from '../milestones/MilestoneDateLabels';
import { InteractiveMilestoneStatusBadge } from '../milestones/InteractiveMilestoneStatusBadge';
import { MilestoneDetailDrawer, MilestoneNotesIndicator } from '../milestones/MilestoneDetailDrawer';
import {
  findComponentProjectById,
  getComponentProjectDisplayName,
} from '../../config/componentProjects';
import { useGroup } from '../../contexts/GroupContext';
import type { AdminMilestoneView } from '../../stores/adminMilestoneViewStore';
import { showSuccessNotification, showApiErrorNotification } from '../../utils/notifications';
import { useAdminInitiativeLeadsQuery } from '../../hooks/useAdminInitiativeLeadsQuery';
import { filterLeadsForComponentProject, findInitiativeLead, subawardeeMatchesLeadPortfolio } from '../../utils/initiativeLeadUtils';
import {
  buildSubawardeeLabelLookup,
  findComponentProjectIdForSubawardee,
  filterSubawardeeIds,
  getSubawardeeDisplayLabel,
  subawardeeBelongsToComponentProject,
} from '../../utils/adminSubawardeeUtils';
import {
  getKeyActivitySubawardeeLabels,
  getMilestoneSubawardeeDisplay,
  getObjectiveSubawardeeLabels,
} from '../../utils/milestoneSubawardee';
import { applyInitiativeLeadFilter } from '../../utils/milestoneInitiativeLeadFilter';
import { formatMilestonesLastUpdatedLabel } from '../../utils/milestoneFreshness';
import { getMilestonePastDueAccent } from '../../utils/milestonePastDue';
// Status configuration
const statusConfig = {
  not_started: { label: 'Not Started', color: 'dark' },
  in_progress: { label: 'In Progress', color: 'blue' },
  blocked: { label: 'Blocked', color: 'red' },
  completed: { label: 'Completed', color: 'green' }
};

type MilestonePathContext = {
  objective_id: string;
  key_activity_id: string;
  goal_id: string;
  activity_id: string;
  milestone_id: string;
};

function collectMilestones(objectives: Objective[]): Milestone[] {
  const milestones: Milestone[] = [];
  objectives.forEach((obj) => {
    obj.key_activities.forEach((ka) => {
      ka.goals.forEach((goal) => {
        goal.activities.forEach((activity) => {
          milestones.push(...activity.milestones);
        });
      });
    });
  });
  return milestones;
}

function calculateCompletionPercentage(milestones: Milestone[]): number {
  if (milestones.length === 0) return 0;
  const completedCount = milestones.filter((m) => m.status === 'completed').length;
  return Math.round((completedCount / milestones.length) * 100);
}

function countOutstandingMilestones(milestones: Milestone[]): number {
  return milestones.filter((m) => m.status !== 'completed').length;
}

function outstandingCountLabel(count: number): string {
  return `${count} outstanding`;
}

function applyMilestoneStatusOptimisticUpdate(
  data: MilestoneData,
  status: MilestoneStatus,
  context: MilestonePathContext,
  options: { matchSubawardee?: string; matchComponentProject?: string } = {}
): MilestoneData {
  const { matchSubawardee, matchComponentProject } = options;
  return {
    ...data,
    objectives: data.objectives.map((objective) => {
      if (objective.id !== context.objective_id) return objective;
      if (
        matchComponentProject &&
        objective.component_project &&
        objective.component_project !== matchComponentProject
      ) {
        return objective;
      }
      return {
        ...objective,
        key_activities: objective.key_activities.map((keyActivity) => {
          if (keyActivity.id !== context.key_activity_id) return keyActivity;
          return {
            ...keyActivity,
            goals: keyActivity.goals.map((goal) => {
              if (goal.id !== context.goal_id) return goal;
              return {
                ...goal,
                activities: goal.activities.map((activity) => {
                  if (activity.id !== context.activity_id) return activity;
                  return {
                    ...activity,
                    milestones: activity.milestones.map((milestone) => {
                      if (milestone.id !== context.milestone_id) return milestone;
                      const sameSubawardee =
                        !matchSubawardee ||
                        !milestone.subawardee ||
                        milestone.subawardee === matchSubawardee;
                      if (!sameSubawardee) return milestone;
                      const sameComponentProject =
                        !matchComponentProject ||
                        !milestone.component_project ||
                        milestone.component_project === matchComponentProject;
                      if (!sameComponentProject) return milestone;
                      return {
                        ...milestone,
                        status,
                        completed: status === 'completed',
                        status_updated_at: new Date().toISOString(),
                      };
                    }),
                  };
                }),
              };
            }),
          };
        }),
      };
    }),
  };
}

function objectiveOptionValue(objective: Objective, includeProject: boolean): string {
  if (includeProject && objective.component_project) {
    return `${objective.component_project}:${objective.id}`;
  }
  return objective.id;
}

function objectiveMatchesSelection(
  objective: Objective,
  selectedObjective: string,
  includeProject: boolean
): boolean {
  if (selectedObjective === 'all') return true;
  if (!includeProject) return objective.id === selectedObjective;
  return objectiveOptionValue(objective, true) === selectedObjective;
}

const SubawardeeBadge = ({ label }: { label: string }) => (
  <Badge size="xs" color="gray" variant="outline">
    {label}
  </Badge>
);

// New component for displaying milestones with status selector
const MilestoneCard = ({
  milestone,
  activityId,
  goalId,
  keyActivityId,
  objectiveId,
  onStatusChange,
  showSubawardee,
  includeSubawardeeInStatusKey,
  includeComponentProjectInStatusKey,
  keyActivitySubawardeeLabels,
  subawardeeLabelById = {},
  fallbackSubawardeeLabel = null,
}: {
  milestone: Milestone;
  activityId: string;
  goalId: string;
  keyActivityId: string;
  objectiveId: string;
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
  showSubawardee?: boolean;
  includeSubawardeeInStatusKey?: boolean;
  includeComponentProjectInStatusKey?: boolean;
  keyActivitySubawardeeLabels?: string[];
  subawardeeLabelById?: Record<string, string>;
  /** Used when milestone/key-activity have no subawardee (e.g. single-subawardee admin view). */
  fallbackSubawardeeLabel?: string | null;
}) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const pastDueAccent = getMilestonePastDueAccent(milestone.status, milestone.due_date);
  const hasSecondaryDateLabel = milestoneHasSecondaryDateLabel(milestone);
  const subawardeeLabel =
    getMilestoneSubawardeeDisplay(milestone, keyActivitySubawardeeLabels ?? [], subawardeeLabelById) ??
    fallbackSubawardeeLabel ??
    null;
  
  // Extract numbers from activity_id and milestone id to create "1.1" format
  const getActivityNumber = (activityId: string): string => {
    const match = activityId.match(/\d+/);
    return match ? parseInt(match[0], 10).toString() : '0';
  };
  
  const getMilestoneNumber = (milestoneId: string): string => {
    const match = milestoneId.match(/\d+/);
    return match ? parseInt(match[0], 10).toString() : '0';
  };
  
  const activityNumber = getActivityNumber(activityId);
  const milestoneNumber = getMilestoneNumber(milestone.id);
  const milestoneLabel = `${activityNumber}.${milestoneNumber}`;
  
    const openDetails = () => setDetailOpen(true);
  const cardAriaLabel = pastDueAccent
    ? `Milestone ${milestoneLabel}, Past Due. Open details`
    : `Milestone ${milestoneLabel}. Open details`;

    return (
    <>
    <Paper 
      shadow="xs" 
      p="md" 
      radius="md" 
      mb="xs"
      ml="lg"
      role="button"
      tabIndex={0}
      aria-label={cardAriaLabel}
      onClick={openDetails}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDetails();
        }
      }}
      sx={(theme) => ({
        cursor: 'pointer',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
        ...(pastDueAccent
          ? { borderLeft: `3px solid ${theme.colors[pastDueAccent][6]}` }
          : {}),
        '&:hover': {
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1]
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6]}`,
          outlineOffset: 2,
        },
        '@media (max-width: 768px)': {
          marginLeft: theme.spacing.sm,
          padding: theme.spacing.sm,
        }
      })}
    >
        <Stack
          spacing="sm"
          pb={hasSecondaryDateLabel ? 0 : MILESTONE_SECONDARY_DATE_ROW_OFFSET}
        >          <Group position="apart" spacing="md" noWrap sx={(theme) => ({
            '@media (max-width: 768px)': {
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: theme.spacing.sm
            }
          })}>
            <Stack spacing={5} style={{ flex: 1, minWidth: 0 }}>
              <Group spacing="xs" mb={4} sx={{ flexWrap: 'wrap' }}>
                <Badge size="xs" color="gray" variant="filled">{`Milestone ${milestoneLabel}`}</Badge>
                {showSubawardee && subawardeeLabel ? <SubawardeeBadge label={subawardeeLabel} /> : null}
                <MilestoneNotesIndicator notesCount={milestone.notes_count ?? 0} />
              </Group>
            <Text 
              size="sm" 
              weight={600}
              sx={(theme) => ({
                textDecoration: milestone.status === 'completed' ? 'line-through' : 'none',
                color: milestone.status === 'completed' ? theme.colors.gray[6] : 'inherit',
                lineClamp: 2,
                '@media (max-width: 768px)': {
                  fontSize: theme.fontSizes.xs,
                  lineClamp: 'unset',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }
              })}
            >
              {milestone.title}
            </Text>
          </Stack>
          <Stack spacing="xs" align="flex-end" sx={(theme) => ({
            '@media (max-width: 768px)': {
              alignItems: 'flex-start'
            }
          })}>
            <MilestoneDateLabels milestone={milestone} />
            <Box
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <InteractiveMilestoneStatusBadge
                status={milestone.status}
                statusUpdatedAt={
                  milestone.status_updated_at ??
                  (milestone as { statusUpdatedAt?: string }).statusUpdatedAt
                }
                onStatusChange={(status) => {
                  const componentProjectSuffix =
                    includeComponentProjectInStatusKey && milestone.component_project
                      ? `:${milestone.component_project}`
                      : '';
                  const subawardeeSuffix =
                    includeSubawardeeInStatusKey && milestone.subawardee
                      ? `:${milestone.subawardee}`
                      : '';
                  onStatusChange(
                    `${milestone.id}:${activityId}:${goalId}:${keyActivityId}:${objectiveId}${componentProjectSuffix}${subawardeeSuffix}`,
                    status
                  );
                }}
              />
            </Box>
          </Stack>
        </Group>
      </Stack>
    </Paper>
    <MilestoneDetailDrawer
      opened={detailOpen}
      onClose={() => setDetailOpen(false)}
      milestone={milestone}
      path={{
        objective_id: objectiveId,
        key_activity_id: keyActivityId,
        goal_id: goalId,
        activity_id: activityId,
      }}
      milestoneLabel={`Milestone ${milestoneLabel}`}
      subawardeeLabel={subawardeeLabel}
      scope={{
        componentProjectId: includeComponentProjectInStatusKey
          ? milestone.component_project
          : undefined,
        subawardeeForScope: includeComponentProjectInStatusKey
          ? undefined
          : milestone.subawardee,
      }}
      onStatusChange={(status) => {
        const componentProjectSuffix =
          includeComponentProjectInStatusKey && milestone.component_project
            ? `:${milestone.component_project}`
            : '';
        const subawardeeSuffix =
          includeSubawardeeInStatusKey && milestone.subawardee
            ? `:${milestone.subawardee}`
            : '';
        onStatusChange(
          `${milestone.id}:${activityId}:${goalId}:${keyActivityId}:${objectiveId}${componentProjectSuffix}${subawardeeSuffix}`,
          status
        );
      }}
    />
    </>
  );
};

const ActivityCard = ({
  activity,
  goalId,
  keyActivityId,
  objectiveId,
  onStatusChange,
  showSubawardee,
  includeSubawardeeInStatusKey,
  includeComponentProjectInStatusKey,
  keyActivitySubawardeeLabels,
  subawardeeLabelById = {},
  fallbackSubawardeeLabel = null,
}: {
  activity: Activity;
  goalId: string;
  keyActivityId: string;
  objectiveId: string;
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
  showSubawardee?: boolean;
  includeSubawardeeInStatusKey?: boolean;
  includeComponentProjectInStatusKey?: boolean;
  keyActivitySubawardeeLabels?: string[];
  subawardeeLabelById?: Record<string, string>;
  fallbackSubawardeeLabel?: string | null;
}) => {
  const [expanded, setExpanded] = useState(true);
  
  const completedCount = activity.milestones.filter(m => m.status === 'completed').length;
  const completionPercentage = activity.milestones.length > 0 
    ? Math.round((completedCount / activity.milestones.length) * 100) 
    : 0;
  
  return (
    <Box mb="md" ml="lg">
      <Stack spacing="xs">
        {/* Activity Label - aligned with text content */}
        <Badge size="sm" color="cyan" variant="light" style={{ alignSelf: 'flex-start', marginLeft: '32px' }}>
          ACTIVITY
        </Badge>
        
        <Group position="apart" spacing="md" noWrap sx={(theme) => ({
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing.xs
          }
        })}>
          <Group spacing="xs" noWrap style={{ flex: 1 }}>
            <ActionIcon 
              onClick={() => setExpanded(!expanded)} 
              aria-label={expanded ? 'Collapse activity' : 'Expand activity'}
            >
              {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            </ActionIcon>
            <Text weight={500} size="sm">
              {activity.title}
            </Text>
          </Group>
          <Text size="xs" color="dimmed" sx={(theme) => ({
            '@media (max-width: 768px)': {
              marginLeft: theme.spacing.xl
            }
          })}>{`${completedCount}/${activity.milestones.length} complete`}</Text>
        </Group>
        
        <Progress 
          value={completionPercentage} 
          size="sm" 
          radius="xl" 
          color={completionPercentage === 100 ? 'teal' : 'blue'}
        />
        
        <Collapse in={expanded}>
          <Stack spacing="xs" mt="xs">
            {activity.milestones.length > 0 ? (
              activity.milestones.map((milestone) => (
                <MilestoneCard
                  key={`${objectiveId}:${keyActivityId}:${goalId}:${activity.id}:${milestone.id}`}
                  milestone={milestone}
                  activityId={activity.id}
                  goalId={goalId}
                  keyActivityId={keyActivityId}
                  objectiveId={objectiveId}
                  onStatusChange={onStatusChange}
                  showSubawardee={showSubawardee}
                  includeSubawardeeInStatusKey={includeSubawardeeInStatusKey}
                  includeComponentProjectInStatusKey={includeComponentProjectInStatusKey}
                  keyActivitySubawardeeLabels={keyActivitySubawardeeLabels}
                  subawardeeLabelById={subawardeeLabelById}
                  fallbackSubawardeeLabel={fallbackSubawardeeLabel}
                />
              ))
            ) : (
              <Text size="sm" color="dimmed" italic>No milestones found for this activity</Text>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
};



const KeyActivityCard = ({
  keyActivity,
  objectiveId,
  onStatusChange,
  showSubawardee,
  includeSubawardeeInStatusKey,
  includeComponentProjectInStatusKey,
  subawardeeLabelById = {},
  fallbackSubawardeeLabel = null,
}: {
  keyActivity: KeyActivity;
  objectiveId: string;
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
  showSubawardee?: boolean;
  includeSubawardeeInStatusKey?: boolean;
  includeComponentProjectInStatusKey?: boolean;
  subawardeeLabelById?: Record<string, string>;
  fallbackSubawardeeLabel?: string | null;
}) => {
  const [expanded, setExpanded] = useState(true);
  const keyActivitySubawardeeLabels = getKeyActivitySubawardeeLabels(keyActivity, subawardeeLabelById);
  
  const allMilestones = keyActivity.goals.flatMap(goal => 
    goal.activities.flatMap(activity => activity.milestones)
  );
  const completedCount = allMilestones.filter(m => m.status === 'completed').length;
  const completionPercentage = allMilestones.length > 0 
    ? Math.round((completedCount / allMilestones.length) * 100) 
    : 0;
  
  return (
    <Box mb="md" ml="lg">
      <Stack spacing="xs">
        <Group
          spacing="xs"
          sx={(theme) => ({
            alignSelf: 'flex-start',
            marginLeft: '32px',
            flexWrap: 'wrap',
            '@media (max-width: 768px)': { marginLeft: theme.spacing.xl },
          })}
        >
          <Badge size="md" color="indigo" variant="light">
            KEY ACTIVITY
          </Badge>
          {showSubawardee
            ? keyActivitySubawardeeLabels.map((label) => (
                <SubawardeeBadge key={label} label={label} />
              ))
            : null}
        </Group>

        <Group position="apart" spacing="md" noWrap sx={(theme) => ({
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing.xs
          }
        })}>
          <Group spacing="xs" noWrap style={{ flex: 1 }}>
            <ActionIcon 
              onClick={() => setExpanded(!expanded)} 
              aria-label={expanded ? 'Collapse key activity' : 'Expand key activity'}
            >
              {expanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
            </ActionIcon>
            <Text weight={600} size="md">
              {keyActivity.title}
            </Text>
          </Group>
          <Text size="xs" color="dimmed" sx={(theme) => ({
            '@media (max-width: 768px)': {
              marginLeft: theme.spacing.xl
            }
          })}>{`${completedCount}/${allMilestones.length} milestones complete`}</Text>
        </Group>
        
        <Progress 
          value={completionPercentage} 
          size="sm" 
          radius="xl" 
          color={completionPercentage === 100 ? 'teal' : 'blue'}
        />
        
        <Collapse in={expanded}>
          <Stack spacing="md" mt="xs">
            {keyActivity.goals.flatMap((goal) => 
              goal.activities.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  goalId={goal.id}
                  keyActivityId={keyActivity.id}
                  objectiveId={objectiveId}
                  onStatusChange={onStatusChange}
                  showSubawardee={showSubawardee}
                  includeSubawardeeInStatusKey={includeSubawardeeInStatusKey}
                  includeComponentProjectInStatusKey={includeComponentProjectInStatusKey}
                  keyActivitySubawardeeLabels={keyActivitySubawardeeLabels}
                  subawardeeLabelById={subawardeeLabelById}
                  fallbackSubawardeeLabel={fallbackSubawardeeLabel}
                />
              ))
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
};

const ObjectiveCard = ({
  objective,
  originalObjective,
  onStatusChange,
  showSubawardee,
  showObjectiveSubawardee,
  includeSubawardeeInStatusKey,
  includeComponentProjectInStatusKey,
  defaultExpanded = false,
  subawardeeLabelById = {},
  fallbackSubawardeeLabel = null,
}: {
  objective: Objective;
  originalObjective: Objective;
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
  showSubawardee?: boolean;
  showObjectiveSubawardee?: boolean;
  includeSubawardeeInStatusKey?: boolean;
  includeComponentProjectInStatusKey?: boolean;
  defaultExpanded?: boolean;
  subawardeeLabelById?: Record<string, string>;
  fallbackSubawardeeLabel?: string | null;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const objectiveSubawardeeLabels = showObjectiveSubawardee
    ? getObjectiveSubawardeeLabels(objective, subawardeeLabelById)
    : [];
  
  // Use originalObjective for completion calculations to get true percentage
  const allMilestones = originalObjective.key_activities.flatMap(ka => 
    ka.goals.flatMap(goal => 
      goal.activities.flatMap(activity => activity.milestones)
    )
  );
  const completedCount = allMilestones.filter(m => m.status === 'completed').length;
  const completionPercentage = allMilestones.length > 0 
    ? Math.round((completedCount / allMilestones.length) * 100) 
    : 0;
  // Count from the displayed (filtered) tree so the header matches what’s inside.
  const outstandingCount = countOutstandingMilestones(
    objective.key_activities.flatMap((ka) =>
      ka.goals.flatMap((goal) => goal.activities.flatMap((activity) => activity.milestones))
    )
  );
  
  return (
    <Card shadow="sm" p="xl" radius="lg" withBorder mb="xl" bg="rgba(0,0,0,0.02)">
      <Stack spacing="lg">
        <Group position="apart" spacing="md" noWrap align="flex-start">
          <Group spacing="md" noWrap style={{ flex: 1, minWidth: 0 }}>
            <ActionIcon 
              onClick={() => setExpanded(!expanded)} 
              aria-label={expanded ? 'Collapse objective' : 'Expand objective'}
              size="lg"
            >
              {expanded ? <IconChevronDown size={24} /> : <IconChevronRight size={24} />}
            </ActionIcon>
            <Stack spacing={4} sx={{ minWidth: 0 }}>
              <Group spacing="md" sx={{ flexWrap: 'wrap' }}>
                <Badge size="lg" color="violet" variant="light">
                  OBJECTIVE
                </Badge>
                {objectiveSubawardeeLabels.map((label) => (
                  <SubawardeeBadge key={label} label={label} />
                ))}
                <Badge color={completionPercentage === 100 ? 'green' : 'blue'} variant="filled">
                  {completionPercentage}% Complete
                </Badge>
              </Group>
              <Title order={3}>
                {objective.title}
              </Title>
              {objective.timeframe && (
                <Text size="sm" color="dimmed">
                  Timeframe: {objective.timeframe}
                </Text>
              )}
            </Stack>
          </Group>
          {!expanded && outstandingCount > 0 ? (
            <Text size="sm" color="dimmed" weight={500} sx={{ flexShrink: 0 }}>
              {outstandingCountLabel(outstandingCount)}
            </Text>
          ) : null}
        </Group>

        {!showObjectiveSubawardee && (
          <Progress
            value={completionPercentage}
            size="lg"
            radius="xl"
            color={completionPercentage === 100 ? 'green' : 'blue'}
          />
        )}

        <Collapse in={expanded}>
          {showObjectiveSubawardee && (
            <Progress
              value={completionPercentage}
              size="lg"
              radius="xl"
              color={completionPercentage === 100 ? 'green' : 'blue'}
              mb="md"
            />
          )}
          <Stack spacing="xl">
            {objective.key_activities.map((keyActivity) => (
              <KeyActivityCard
                key={keyActivity.id}
                keyActivity={keyActivity}
                objectiveId={objective.id}
                onStatusChange={onStatusChange}
                showSubawardee={showSubawardee}
                includeSubawardeeInStatusKey={includeSubawardeeInStatusKey}
                includeComponentProjectInStatusKey={includeComponentProjectInStatusKey}
                subawardeeLabelById={subawardeeLabelById}
                fallbackSubawardeeLabel={fallbackSubawardeeLabel}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
};

/** Collapsible per-project section used when no component project is selected. */
const ComponentProjectMilestonesSection = ({
  componentProjectId,
  objectives,
  originalObjectives,
  onStatusChange,
  showSubawardee,
  includeComponentProjectInStatusKey,
  subawardeeLabelById = {},
  fallbackSubawardeeLabel = null,
  defaultExpanded = false,
}: {
  componentProjectId: string;
  objectives: Objective[];
  originalObjectives: Objective[];
  onStatusChange: (milestoneId: string, status: MilestoneStatus) => void;
  showSubawardee?: boolean;
  includeComponentProjectInStatusKey?: boolean;
  subawardeeLabelById?: Record<string, string>;
  fallbackSubawardeeLabel?: string | null;
  defaultExpanded?: boolean;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const projectName =
    getComponentProjectDisplayName(componentProjectId) || componentProjectId;
  const panelId = `milestones-panel-${componentProjectId}`;
  const projectMilestones = collectMilestones(objectives);
  const completionPercentage = calculateCompletionPercentage(
    collectMilestones(originalObjectives)
  );
  const outstandingCount = countOutstandingMilestones(projectMilestones);

  return (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      role="article"
      aria-label={
        outstandingCount > 0
          ? `${projectName} milestones, ${outstandingCountLabel(outstandingCount)}`
          : `${projectName} milestones`
      }
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md,
        '@media (max-width: 768px)': {
          margin: theme.spacing.sm,
        },
      })}
    >
      <Stack spacing="sm">
        <UnstyledButton
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls={panelId}
          sx={(theme) => ({
            width: '100%',
            display: 'block',
            textAlign: 'left',
            color: theme.colorScheme === 'dark' ? theme.colors.gray[0] : theme.colors.dark[9],
            borderRadius: theme.radius.sm,
            padding: theme.spacing.xs,
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor:
                theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            },
            '&:focus-visible': {
              outline: `2px solid ${
                theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6]
              }`,
              outlineOffset: 2,
            },
          })}
        >
          <Stack spacing="sm">
            <Group spacing="sm" noWrap>
              {expanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
              <Text weight={600} size="lg" lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                {projectName}
              </Text>
              <Badge color={completionPercentage === 100 ? 'green' : 'blue'} variant="filled">
                {completionPercentage}% Complete
              </Badge>
            </Group>
            <Box pl={26}>
              <Progress
                value={completionPercentage}
                size="md"
                radius="xl"
                color={completionPercentage === 100 ? 'green' : 'blue'}
              />
              <Group position="apart" align="center" noWrap mt={6}>
                <Text size="xs" color="dimmed">
                  {objectives.length} objective{objectives.length === 1 ? '' : 's'}
                </Text>
                {!expanded && outstandingCount > 0 ? (
                  <Text size="xs" color="dimmed" weight={500}>
                    {outstandingCountLabel(outstandingCount)}
                  </Text>
                ) : null}
              </Group>
            </Box>
          </Stack>
        </UnstyledButton>

        <Collapse in={expanded}>
          <Box id={panelId} mt="xs">
            {objectives.length > 0 ? (
              <Stack spacing="md">
                {objectives.map((objective) => {
                  const originalObjective =
                    originalObjectives.find((orig) => orig.id === objective.id) || objective;
                  return (
                    <ObjectiveCard
                      key={`${componentProjectId}:${objective.id}`}
                      objective={objective}
                      originalObjective={originalObjective}
                      onStatusChange={onStatusChange}
                      showSubawardee={showSubawardee}
                      defaultExpanded={false}
                      includeComponentProjectInStatusKey={includeComponentProjectInStatusKey}
                      subawardeeLabelById={subawardeeLabelById}
                      fallbackSubawardeeLabel={fallbackSubawardeeLabel}
                    />
                  );
                })}
              </Stack>
            ) : (
              <Text size="sm" color="dimmed" pl={26}>
                No milestones found for this component project.
              </Text>
            )}
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  );
};

// Skeleton components for loading states
const MilestoneSkeleton = () => (
  <Paper p="md" shadow="xs" radius="md" mb="xs" ml="lg">
    <Stack spacing="sm">
      <Group position="apart" spacing="md" noWrap>
        <Stack spacing={5} style={{ flex: 1 }}>
          <Group spacing="xs" mb={4}>
            <Skeleton height={20} width={80} radius="xl" />
          </Group>
          <Skeleton height={16} width="85%" />
          <Skeleton height={16} width="70%" />
        </Stack>
        <Group spacing="xs" noWrap>
          <Skeleton height={16} width={16} />
          <Skeleton height={16} width={80} />
        </Group>
      </Group>
      <Group position="apart" spacing="md" noWrap>
        <Skeleton height={28} width={120} />
        <Group spacing="xs">
          <Skeleton height={24} width={60} radius="xl" />
        </Group>
      </Group>
    </Stack>
  </Paper>
);

const ActivitySkeleton = () => (
  <Box mb="md" ml="lg">
    <Stack spacing="xs">
      <Group position="apart" spacing="md" noWrap>
        <Group spacing="xs" noWrap style={{ flex: 1 }}>
          <Skeleton height={16} width={16} />
          <Skeleton height={20} width="60%" />
        </Group>
        <Skeleton height={16} width={100} />
      </Group>
      <Skeleton height={8} radius="xl" />
      <Stack spacing="xs" mt="xs">
        <MilestoneSkeleton />
        <MilestoneSkeleton />
        <MilestoneSkeleton />
      </Stack>
    </Stack>
  </Box>
);

const GoalSkeleton = () => (
  <Box mb="xl" ml="lg">
    <Stack spacing="md">
      <Group position="apart" spacing="md" noWrap>
        <Group spacing="xs" noWrap style={{ flex: 1 }}>
          <Skeleton height={20} width={20} />
          <Skeleton height={24} width="70%" />
        </Group>
        <Skeleton height={20} width={120} />
      </Group>
      <Skeleton height={12} radius="xl" />
      <Stack spacing="lg" mt="xs">
        <ActivitySkeleton />
        <ActivitySkeleton />
      </Stack>
    </Stack>
  </Box>
);

const KeyActivitySkeleton = () => (
  <Box mb="md" ml="lg">
    <Stack spacing="xs">
      <Group position="apart" spacing="md" noWrap>
        <Group spacing="xs" noWrap style={{ flex: 1 }}>
          <Skeleton height={18} width={18} />
          <Skeleton height={22} width="65%" />
        </Group>
        <Skeleton height={18} width={110} />
      </Group>
      <Skeleton height={8} radius="xl" />
      <Stack spacing="md" mt="xs">
        <GoalSkeleton />
      </Stack>
    </Stack>
  </Box>
);

const ObjectiveSkeleton = () => (
  <Card shadow="sm" p="xl" radius="lg" withBorder mb="xl" bg="rgba(0,0,0,0.02)">
    <Stack spacing="lg">
      <Group position="apart" spacing="md" noWrap>
        <Group spacing="md" noWrap style={{ flex: 1 }}>
          <Skeleton height={24} width={24} />
          <Stack spacing={4}>
            <Group spacing="xs">
              <Skeleton height={28} width={120} radius="xl" />
            </Group>
            <Skeleton height={32} width="80%" />
            <Skeleton height={20} width="40%" />
          </Stack>
        </Group>
      </Group>
      <Skeleton height={12} radius="xl" />
      <Stack spacing="xl" mt="xs">
        <KeyActivitySkeleton />
        <KeyActivitySkeleton />
      </Stack>
    </Stack>
  </Card>
);

export function AdminMilestonesPage() {
  const {
    view,
    setView,
    componentProject,
    showPageComponentProjectFilter,
    setComponentProject,
    subawardee,
    setSubawardee,
  } = useAdminMilestoneViewUrlState();

  const [initiativeLeadFilter, setInitiativeLeadFilter] = useState<string | null>(null);

  const { data: initiativeLeads = [], isSuccess: initiativeLeadsReady } = useAdminInitiativeLeadsQuery();
  const { availableGroups } = useGroup();
  const subawardeeLabelById = useMemo(
    () => buildSubawardeeLabelLookup(availableGroups),
    [availableGroups]
  );

  const subawardeeScopeIds = useMemo(
    () => filterSubawardeeIds(initiativeLeads, { componentProjectId: componentProject }),
    [initiativeLeads, componentProject]
  );

  const subawardeeInProjectScope =
    !subawardee ||
    subawardeeBelongsToComponentProject(subawardee, componentProject, initiativeLeads);

  const showAllSubawardees = view === 'subawardee' && !subawardee;
  const showAllComponentProjects = view === 'component' && !componentProject;
  const showSubawardeeBadges = view === 'component' || showAllSubawardees;
  const fallbackSubawardeeLabel = subawardee
    ? getSubawardeeDisplayLabel(subawardee, subawardeeLabelById) ?? subawardee
    : null;

  /** Component tab loads all projects when none selected; sub-awardee tab works with or without a project. */
  const scopeReady =
    view === 'component'
      ? true
      : Boolean(subawardee) || subawardeeScopeIds.length > 0;
  const milestonesQueryEnabled =
    scopeReady &&
    initiativeLeadsReady &&
    (view === 'component' ||
      (subawardee ? subawardeeInProjectScope : subawardeeScopeIds.length > 0));

  useSyncSelectedGroupForMilestoneView(view, componentProject, subawardee);

  const handleViewChange = (nextView: AdminMilestoneView) => {
    if (!componentProject && subawardee) {
      const projectId = findComponentProjectIdForSubawardee(subawardee, initiativeLeads);
      if (projectId) setComponentProject(projectId);
    }
    setView(nextView);
  };

  const componentProjectsQuery = useQuery({
    queryKey: ['admin-milestones', 'component', componentProject ?? 'all'],
    queryFn: async (): Promise<ComponentProjectMilestoneData[]> => {
      if (componentProject) {
        const data = await milestonesApi.getMilestonesForComponentProject(componentProject);
        return [{ componentProjectId: componentProject, data }];
      }
      return getMilestonesForAllComponentProjects();
    },
    enabled: view === 'component' && milestonesQueryEnabled,
    staleTime: 2 * 60 * 1000,
  });

  const subawardeeMilestonesQuery = useQuery({
    queryKey: subawardee
      ? ['admin-milestones', 'subawardee', componentProject, subawardee]
      : ['admin-milestones', 'subawardee', 'all', componentProject, subawardeeScopeIds.join(',')],
    queryFn: () => {
      if (subawardee) {
        return milestonesApi.getMilestonesForSubawardee(subawardee);
      }
      return getMilestonesForAllSubawardees(subawardeeScopeIds);
    },
    enabled: view === 'subawardee' && milestonesQueryEnabled,
    staleTime: 2 * 60 * 1000,
  });

  const isLoading =
    view === 'component' ? componentProjectsQuery.isLoading : subawardeeMilestonesQuery.isLoading;
  const error =
    view === 'component' ? componentProjectsQuery.error : subawardeeMilestonesQuery.error;
  const milestoneData =
    view === 'subawardee' ? subawardeeMilestonesQuery.data : undefined;
  const componentProjectGroups = useMemo(
    () => (view === 'component' ? componentProjectsQuery.data ?? [] : []),
    [view, componentProjectsQuery.data]
  );

  const queryClient = useQueryClient();

  const updateMilestoneStatusMutation = useMutation({
    mutationFn: ({
      milestoneId,
      status,
      context,
      subawardeeScope,
      componentProjectScope,
    }: {
      milestoneId: string;
      status: MilestoneStatus;
      context: MilestonePathContext;
      subawardeeScope?: string;
      componentProjectScope?: string;
    }) =>
      view === 'component'
        ? milestonesApi.updateComponentProjectMilestoneStatus(
            componentProjectScope ?? componentProject!,
            milestoneId,
            status,
            context
          )
        : milestonesApi.updateMilestoneStatus(
            milestoneId,
            status,
            context,
            subawardeeScope ?? subawardee!
          ),
    onMutate: async ({ status, context, subawardeeScope, componentProjectScope }) => {
      await queryClient.cancelQueries({ queryKey: ['admin-milestones'] });
      const matchSubawardee = subawardeeScope ?? undefined;
      const matchComponentProject = componentProjectScope ?? undefined;

      if (view === 'component') {
        const queryKey = ['admin-milestones', 'component', componentProject ?? 'all'] as const;
        const previous = queryClient.getQueryData<ComponentProjectMilestoneData[]>(queryKey);
        queryClient.setQueryData<ComponentProjectMilestoneData[]>(queryKey, (current) => {
          if (!current) return current;
          return current.map((project) => {
            if (
              matchComponentProject &&
              project.componentProjectId !== matchComponentProject
            ) {
              return project;
            }
            return {
              ...project,
              data: applyMilestoneStatusOptimisticUpdate(project.data, status, context, {
                matchSubawardee,
                matchComponentProject,
              }),
            };
          });
        });
        return { previous, queryKey };
      }

      const queryKey = subawardee
        ? (['admin-milestones', 'subawardee', componentProject, subawardee] as const)
        : ([
            'admin-milestones',
            'subawardee',
            'all',
            componentProject,
            subawardeeScopeIds.join(','),
          ] as const);
      const previous = queryClient.getQueryData<MilestoneData>(queryKey);
      queryClient.setQueryData<MilestoneData>(queryKey, (current) => {
        if (!current) return current;
        return applyMilestoneStatusOptimisticUpdate(current, status, context, {
          matchSubawardee,
        });
      });
      return { previous, queryKey };
    },
    onSuccess: (_, { status }) => {
      showSuccessNotification(`Milestone status updated to ${status}`);
      queryClient.invalidateQueries({ queryKey: ['admin-milestones'] });
    },
    onError: (error, _vars, context) => {
      if (context?.previous && context.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
      showApiErrorNotification(error, 'Failed to update milestone status');
    }
  });

  const objectives = useMemo(() => {
    if (view === 'component') {
      return componentProjectGroups.flatMap((project) => project.data.objectives);
    }
    return milestoneData?.objectives || [];
  }, [view, componentProjectGroups, milestoneData]);

  const leadFilteredObjectives = useMemo(
    () =>
      applyInitiativeLeadFilter(objectives, initiativeLeadFilter, initiativeLeads, {
        view,
        subawardee,
      }),
    [objectives, initiativeLeadFilter, initiativeLeads, view, subawardee]
  );

  // Handle milestone status updates
  const updateMilestoneStatus = async (compositeId: string, status: MilestoneStatus): Promise<void> => {
    const [
      milestoneId,
      activityId,
      goalId,
      keyActivityId,
      objectiveId,
      scopePart,
    ] = compositeId.split(':');

    await updateMilestoneStatusMutation.mutateAsync({
      milestoneId,
      status,
      context: {
        objective_id: objectiveId,
        key_activity_id: keyActivityId,
        goal_id: goalId,
        activity_id: activityId,
        milestone_id: milestoneId,
      },
      componentProjectScope: view === 'component' ? scopePart : undefined,
      subawardeeScope: view === 'subawardee' ? scopePart : undefined,
    });
  };
  
  const allMilestones = useMemo(
    () => collectMilestones(leadFilteredObjectives),
    [leadFilteredObjectives]
  );

  const [selectedObjective, setSelectedObjective] = useState<string | null>('all');
  const [selectedStatus, setSelectedStatus] = useState<string | null>('all');

  useEffect(() => {
    setSelectedObjective('all');
    setSelectedStatus('all');
    setInitiativeLeadFilter(null);
  }, [view, componentProject, subawardee]);

  useEffect(() => {
    if (!subawardee || !initiativeLeadFilter) return;
    const lead = findInitiativeLead(initiativeLeads, initiativeLeadFilter);
    if (lead && !subawardeeMatchesLeadPortfolio(subawardee, lead.subawardees)) {
      setSubawardee(null);
    }
  }, [initiativeLeadFilter, subawardee, setSubawardee, initiativeLeads]);

  useEffect(() => {
    if (!componentProject || !subawardee) return;
    if (!subawardeeBelongsToComponentProject(subawardee, componentProject, initiativeLeads)) {
      setSubawardee(null);
    }
  }, [componentProject, subawardee, setSubawardee, initiativeLeads]);

  useEffect(() => {
    if (!initiativeLeadFilter || !componentProject) return;
    const project = findComponentProjectById(componentProject);
    const lead = findInitiativeLead(initiativeLeads, initiativeLeadFilter);
    if (
      project &&
      lead &&
      !lead.component_projects.some(
        (name) =>
          name.toLowerCase() === project.name.toLowerCase() ||
          name.toLowerCase() === project.id.toLowerCase()
      )
    ) {
      setInitiativeLeadFilter(null);
    }
  }, [componentProject, initiativeLeadFilter, initiativeLeads]);

  useEffect(() => {
    if (!initiativeLeadFilter) return;
    const visibleLeads = filterLeadsForComponentProject(initiativeLeads, componentProject, {
      view,
      subawardee,
    });
    if (!visibleLeads.some((lead) => lead.id === initiativeLeadFilter)) {
      setInitiativeLeadFilter(null);
    }
  }, [initiativeLeadFilter, initiativeLeads, componentProject, view, subawardee]);

  useEffect(() => {
    if (!initiativeLeadFilter || !selectedObjective || selectedObjective === 'all') return;
    const stillVisible = leadFilteredObjectives.some((o) =>
      objectiveMatchesSelection(o, selectedObjective, showAllComponentProjects)
    );
    if (!stillVisible) setSelectedObjective('all');
  }, [
    initiativeLeadFilter,
    leadFilteredObjectives,
    selectedObjective,
    showAllComponentProjects,
  ]);

  const objectiveOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All Objectives' },
      ...leadFilteredObjectives.map((objective) => ({
        value: objectiveOptionValue(objective, showAllComponentProjects),
        label: showAllComponentProjects
          ? `${getComponentProjectDisplayName(objective.component_project)} — ${objective.title}`
          : objective.title,
      })),
    ];
  }, [leadFilteredObjectives, showAllComponentProjects]);

  const statusOptions = useMemo(() => {
    return [
      { value: 'all', label: 'All Statuses' },
      ...Object.entries(statusConfig).map(([value, config]) => ({
        value,
        label: config.label
      }))
    ];
  }, []);

  // Filter objectives based on selection and status
  const filteredObjectives = useMemo(() => {
    let filtered = leadFilteredObjectives.filter((objective) =>
      objectiveMatchesSelection(objective, selectedObjective ?? 'all', showAllComponentProjects)
    );

    // Status filtering only; subawardee labels come from the API on component-project trees.
    if (selectedStatus !== 'all') {
      filtered = filtered.map(objective => ({
        ...objective,
        key_activities: objective.key_activities.map(keyActivity => ({
          ...keyActivity,
          goals: keyActivity.goals.map(goal => ({
            ...goal,
            activities: goal.activities.map(activity => ({
              ...activity,
              milestones: activity.milestones.filter(milestone => 
                milestone.status === selectedStatus
              )
            })).filter(activity => activity.milestones.length > 0)
          })).filter(goal => goal.activities.length > 0)
        })).filter(keyActivity => keyActivity.goals.length > 0)
      })).filter(objective => objective.key_activities.length > 0);
    }
    
    return filtered;
  }, [leadFilteredObjectives, selectedObjective, selectedStatus, showAllComponentProjects]);

  const filteredProjectGroups = useMemo(() => {
    if (!showAllComponentProjects) return [];
    return componentProjectGroups.map((project) => {
      const originalObjectives = applyInitiativeLeadFilter(
        project.data.objectives,
        initiativeLeadFilter,
        initiativeLeads,
        { view, subawardee }
      );
      const objectives = filteredObjectives.filter(
        (objective) => objective.component_project === project.componentProjectId
      );
      return {
        componentProjectId: project.componentProjectId,
        originalObjectives,
        objectives,
      };
    });
  }, [
    showAllComponentProjects,
    componentProjectGroups,
    filteredObjectives,
    initiativeLeadFilter,
    initiativeLeads,
    view,
    subawardee,
  ]);

  // Calculate overall completion percentage
  const overallCompletionPercentage = useMemo(() => {
    return calculateCompletionPercentage(allMilestones);
  }, [allMilestones]);

  const progressTitle = useMemo(() => {
    if (view === 'component') {
      const project = findComponentProjectById(componentProject);
      return project ? `${project.name} project progress` : 'All component projects progress';
    }
    if (view === 'subawardee' && !subawardee) {
      const project = findComponentProjectById(componentProject);
      return project ? `All ${project.name} sub-awardees progress` : 'All sub-awardees progress';
    }
    const subLabel = getSubawardeeDisplayLabel(subawardee, subawardeeLabelById);
    return subLabel ? `${subLabel} progress` : 'Sub-awardee progress';
  }, [view, componentProject, subawardee, subawardeeLabelById]);

  const progressSubtitle = `${overallCompletionPercentage}% of milestones completed`;

  const progressLastUpdatedLabel = useMemo(() => {
    if (view === 'component') {
      const metadataBySubawardee: Record<string, ObjectivesMetadata | null> = {};
      for (const project of componentProjectGroups) {
        if (project.data.metadataBySubawardee) {
          Object.assign(metadataBySubawardee, project.data.metadataBySubawardee);
        }
      }
      return formatMilestonesLastUpdatedLabel({
        objectives: leadFilteredObjectives,
        metadataBySubawardee:
          Object.keys(metadataBySubawardee).length > 0 ? metadataBySubawardee : undefined,
      });
    }
    return formatMilestonesLastUpdatedLabel({
      objectives: leadFilteredObjectives,
      metadata: milestoneData?.metadata,
      metadataBySubawardee: milestoneData?.metadataBySubawardee,
    });
  }, [
    view,
    componentProjectGroups,
    leadFilteredObjectives,
    milestoneData?.metadata,
    milestoneData?.metadataBySubawardee,
  ]);

  const filtersProps: AdminMilestonesFiltersProps = {
    view,
    onViewChange: handleViewChange,
    componentProject,
    showComponentProjectFilter: showPageComponentProjectFilter,
    onComponentProjectChange: setComponentProject,
    subawardee,
    onSubawardeeChange: setSubawardee,
    initiativeLeadFilter,
    onInitiativeLeadFilterChange: setInitiativeLeadFilter,
    selectedObjective,
    onSelectedObjectiveChange: setSelectedObjective,
    selectedStatus,
    onSelectedStatusChange: setSelectedStatus,
    objectiveOptions,
    statusOptions,
    initiativeLeads,
    detailFiltersDisabled: !milestonesQueryEnabled || isLoading,
  };

  const filtersPanel = <AdminMilestonesFilters {...filtersProps} />;

  const progressCard = isLoading ? (
    <Paper
      p="md"
      shadow="md"
      radius="md"
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md,
        '@media (max-width: 768px)': {
          margin: theme.spacing.sm,
        },
      })}
    >
      <Group position="apart">
        <div>
          <Skeleton height={28} width={150} mb="xs" />
          <Skeleton height={20} width={200} />
        </div>
        <Skeleton height={120} width={120} radius="50%" />
      </Group>
    </Paper>
  ) : (
    <Paper
      p="md"
      shadow="md"
      radius="md"
      sx={(theme) => ({
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md,
        '@media (max-width: 768px)': {
          margin: theme.spacing.sm,
        },
      })}
    >
      <Group position="apart" sx={(theme) => ({
        '@media (max-width: 480px)': {
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing.md,
        },
      })}>
        <div>
          <Text size="lg" weight={700}>{progressTitle}</Text>
          <Text size="sm" color="dimmed">{progressSubtitle}</Text>
          {progressLastUpdatedLabel ? (
            <Text size="sm" color="dimmed">
              {progressLastUpdatedLabel}
            </Text>
          ) : null}
        </div>
        <RingProgress
          size={120}
          thickness={12}
          roundCaps
          sections={[
            {
              value: overallCompletionPercentage,
              color: overallCompletionPercentage === 100 ? 'green' : 'blue',
            },
          ]}
          label={
            <Center>
              <Text size="xl" weight={700}>
                {overallCompletionPercentage}%
              </Text>
            </Center>
          }
        />
      </Group>
    </Paper>
  );

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
      <PageHeader
        title="Milestones"
        subtitle="Track progress and monitor status of project milestones. Milestones are organized under their associated objective and key activity."
      />

      {filtersPanel}

      {scopeReady &&
        initiativeLeadsReady &&
        showAllSubawardees &&
        componentProject &&
        subawardeeScopeIds.length === 0 && (
        <Alert color="blue" sx={(theme) => ({ '@media (max-width: 768px)': { margin: theme.spacing.sm } })}>
          No sub-awardees are mapped to the selected component project.
        </Alert>
      )}

      {scopeReady && (
        <Group position="apart" mb="xl" sx={(theme) => ({
          '@media (max-width: 768px)': {
            flexDirection: 'column',
            alignItems: 'stretch',
          },
        })}>
          <Stack spacing="md" style={{ flex: 1 }}>
            {progressCard}
          </Stack>
        </Group>
      )}

      {scopeReady && error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          role="alert"
          sx={(theme) => ({
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
            },
          })}
        >
          {error instanceof Error ? error.message : String(error)}
        </Alert>
      )}

      {scopeReady && isLoading && (
        <Stack spacing="xl" sx={(theme) => ({
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
          },
        })}>
          <ObjectiveSkeleton />
          <ObjectiveSkeleton />
          <ObjectiveSkeleton />
        </Stack>
      )}

      {scopeReady && !isLoading && !error && (
        showAllComponentProjects ? (
          filteredProjectGroups.length > 0 ? (
            <Stack spacing="lg" sx={(theme) => ({
              '@media (max-width: 768px)': {
                margin: theme.spacing.sm,
              },
            })}>
              {filteredProjectGroups.map((project) => (
                <ComponentProjectMilestonesSection
                  key={project.componentProjectId}
                  componentProjectId={project.componentProjectId}
                  objectives={project.objectives}
                  originalObjectives={project.originalObjectives}
                  onStatusChange={updateMilestoneStatus}
                  showSubawardee={showSubawardeeBadges}
                  includeComponentProjectInStatusKey
                  subawardeeLabelById={subawardeeLabelById}
                  fallbackSubawardeeLabel={fallbackSubawardeeLabel}
                />
              ))}
            </Stack>
          ) : (
            <Text align="center" size="lg" color="dimmed" sx={(theme) => ({
              '@media (max-width: 768px)': {
                margin: theme.spacing.sm,
                fontSize: theme.fontSizes.md,
              },
            })}>
              No component project milestones found.
            </Text>
          )
        ) : filteredObjectives.length > 0 ? (
          <Stack spacing="xl" sx={(theme) => ({
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
            },
          })}>
            {filteredObjectives.map((objective) => {
              const originalObjective = leadFilteredObjectives.find(
                (orig) =>
                  orig.id === objective.id &&
                  orig.component_project === objective.component_project &&
                  orig.key_activities[0]?.subawardee === objective.key_activities[0]?.subawardee
              );
              const objectiveScopeKey =
                objective.component_project ??
                objective.key_activities[0]?.subawardee ??
                objective.id;
              return (
                <ObjectiveCard
                  key={`${view}:${objectiveScopeKey}:${objective.id}`}
                  objective={objective}
                  originalObjective={originalObjective || objective}
                  onStatusChange={updateMilestoneStatus}
                  showSubawardee={showSubawardeeBadges}
                  showObjectiveSubawardee={view === 'subawardee'}
                  defaultExpanded={view === 'component'}
                  includeSubawardeeInStatusKey={showAllSubawardees}
                  includeComponentProjectInStatusKey={view === 'component'}
                  subawardeeLabelById={subawardeeLabelById}
                  fallbackSubawardeeLabel={fallbackSubawardeeLabel}
                />
              );
            })}
          </Stack>
        ) : (
          <Text align="center" size="lg" color="dimmed" sx={(theme) => ({
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
              fontSize: theme.fontSizes.md,
            },
          })}>
            {selectedStatus !== 'all' && selectedObjective !== 'all'
              ? `No milestones found with status "${statusConfig[selectedStatus as MilestoneStatus]?.label || selectedStatus}" in the selected objective.`
              : selectedStatus !== 'all'
              ? `No milestones found with status "${statusConfig[selectedStatus as MilestoneStatus]?.label || selectedStatus}".`
              : selectedObjective !== 'all'
              ? 'No milestones found in the selected objective.'
              : 'No objectives found.'}
          </Text>
        )
      )}
    </Container>
  );
} 